import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { LibraryEditor } from "./use-library-editor"

import { Database, Search } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"

import { EditorSectionCard, FieldRow, FolderPathInput } from "./editor-fields"
import {
  CREATABLE_SOURCE_TYPES,
  isExternalType,
  isPagesType,
  isSharePointFamily,
} from "./library-editor-model"
import { readSpoField } from "../library-auth-utils"

type SourceSectionProps = {
  editor: LibraryEditor
}

function str(value: unknown): string {
  return typeof value === "string" ? value : ""
}

export function SourceSection({ editor }: SourceSectionProps) {
  const { t } = useTranslation()
  const { values, errors, mode } = editor
  const sourceType = values.source_type
  const config = values.source_config
  // Create: ids come from "Resolve from SharePoint" and stay read-only unless overridden.
  const [showIdFields, setShowIdFields] = useState(false)

  const typeLabels: Record<string, string> = {
    sharepoint: t("adminDashboard.libraries.editor.sourceSharePoint", "SharePoint"),
    spo: t("adminDashboard.libraries.editor.sourceSharePoint", "SharePoint"),
    "spo-avtal": t("adminDashboard.libraries.editor.sourceSpoAvtal", "SharePoint (Avtal)"),
    "spo-list": t("adminDashboard.libraries.editor.sourceSpoList", "SharePoint List"),
    "spo-pages": t("adminDashboard.libraries.editor.sourceSpoPages", "SharePoint Pages"),
    blob: t("adminDashboard.libraries.editor.sourceBlob", "Azure Blob Storage"),
    dynamics: t("adminDashboard.libraries.editor.sourceDynamics", "Dynamics 365"),
    external: t("adminDashboard.libraries.editor.sourceExternal", "External Source"),
    http: t("adminDashboard.libraries.editor.sourceExternal", "External Source"),
  }

  const hostname = str(config.hostname)
  const siteName = readSpoField(config, "siteName")
  const listName = readSpoField(config, "listName")
  const folderPath = readSpoField(config, "folderPath")

  return (
    <EditorSectionCard
      icon={<Database className="size-4" />}
      id="library-source"
      open={editor.openSections.source}
      title={t("adminDashboard.libraries.editor.sections.source", "Source")}
      badge={
        mode === "edit" ? (
          <Badge className="capitalize" variant="secondary">
            {typeLabels[sourceType] ?? sourceType}
          </Badge>
        ) : undefined
      }
      onOpenChange={(open) => editor.setSectionOpen("source", open)}
    >
      {mode === "create" && (
        <FieldRow
          required
          id="source_type"
          label={t("adminDashboard.libraries.editor.sourceType", "Source type")}
        >
          <Select value={sourceType} onValueChange={editor.handleSourceTypeChange}>
            <SelectTrigger id="source_type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CREATABLE_SOURCE_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {typeLabels[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldRow>
      )}

      {isSharePointFamily(sourceType) && mode === "create" && (
        <>
          <FieldRow
            required
            error={errors.sharepoint_url}
            id="sharepoint_url"
            label={t("adminDashboard.libraries.editor.sharepointUrl", "SharePoint URL")}
            help={t(
              "adminDashboard.libraries.editor.sharepointUrlHelp",
              "Full URL to the SharePoint library, list or site you want to index. Site, library and folder are read from it."
            )}
          >
            <Input
              id="sharepoint_url"
              type="url"
              value={str(config.sharepoint_url)}
              placeholder={t(
                "adminDashboard.libraries.editor.sharepointUrlPlaceholder",
                "https://contoso.sharepoint.com/sites/Engineering/Shared Documents"
              )}
              onChange={(event) => editor.handleSharePointUrlChange(event.target.value)}
            />
          </FieldRow>
          {hostname && (
            <div className="bg-muted/50 rounded-md border px-3 py-2 text-xs">
              <p className="text-muted-foreground mb-1 font-medium">
                {t("adminDashboard.libraries.editor.parsedDetails", "Parsed details")}
              </p>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                <span className="text-muted-foreground">
                  {t("adminDashboard.libraries.editor.hostname", "Hostname")}:
                </span>
                <span className="font-mono">{hostname}</span>
                <span className="text-muted-foreground">
                  {t("adminDashboard.libraries.editor.siteName", "Site")}:
                </span>
                <span className="font-mono">{siteName}</span>
                <span className="text-muted-foreground">
                  {t("adminDashboard.libraries.editor.listName", "List / library")}:
                </span>
                <span className="font-mono">{listName}</span>
                {folderPath && (
                  <>
                    <span className="text-muted-foreground">
                      {t("adminDashboard.libraries.editor.folderPath", "Folder")}:
                    </span>
                    <span className="font-mono">{folderPath}</span>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {isSharePointFamily(sourceType) && mode === "edit" && (
        <>
          <FieldRow id="hostname" label={t("adminDashboard.libraries.editor.hostname", "Hostname")}>
            <Input
              id="hostname"
              placeholder="contoso.sharepoint.com"
              value={hostname}
              onChange={(event) => editor.updateSourceConfig("hostname", event.target.value)}
            />
          </FieldRow>
          <FieldRow id="siteName" label={t("adminDashboard.libraries.editor.siteName", "Site")}>
            <Input
              id="siteName"
              placeholder="Engineering"
              value={siteName}
              onChange={(event) => editor.updateSpoField("siteName", event.target.value)}
            />
          </FieldRow>
          <FieldRow
            id="listName"
            label={t("adminDashboard.libraries.editor.listName", "List / library")}
          >
            <Input
              id="listName"
              placeholder="Shared Documents"
              value={listName}
              onChange={(event) => editor.updateSpoField("listName", event.target.value)}
            />
          </FieldRow>
        </>
      )}

      {isSharePointFamily(sourceType) && (mode === "edit" || showIdFields) && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FieldRow
            id="listId"
            label={t("adminDashboard.libraries.editor.listId", "List ID (optional)")}
          >
            <Input
              id="listId"
              placeholder="GUID"
              value={readSpoField(config, "listId")}
              onChange={(event) => editor.updateSpoField("listId", event.target.value)}
            />
          </FieldRow>
          <FieldRow
            id="siteId"
            label={t("adminDashboard.libraries.editor.siteId", "Site ID (optional)")}
          >
            <Input
              id="siteId"
              placeholder="GUID"
              value={readSpoField(config, "siteId")}
              onChange={(event) => editor.updateSpoField("siteId", event.target.value)}
            />
          </FieldRow>
        </div>
      )}

      {isSharePointFamily(sourceType) && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FieldRow
            id="folderPath"
            label={t("adminDashboard.libraries.editor.folderPathField", "Folder inside library")}
            help={t(
              "adminDashboard.libraries.editor.folderPathHelp",
              "Leave empty to index the entire library."
            )}
          >
            <FolderPathInput
              id="folderPath"
              value={folderPath}
              placeholder={t(
                "adminDashboard.libraries.editor.folderPathPlaceholder",
                "e.g., Engineering/Specs"
              )}
              onChange={(next) => editor.updateSpoField("folderPath", next || undefined)}
            />
          </FieldRow>
          <FieldRow
            id="drive_id"
            label={t("adminDashboard.libraries.editor.driveId", "Drive ID (optional)")}
            help={t(
              "adminDashboard.libraries.editor.driveIdHelp",
              "Graph drive id of the document library, when the list name is ambiguous."
            )}
          >
            <Input
              id="drive_id"
              placeholder="b!..."
              value={str(config.drive_id)}
              onChange={(event) =>
                editor.updateSourceConfig("drive_id", event.target.value || undefined)
              }
            />
          </FieldRow>
        </div>
      )}

      {isSharePointFamily(sourceType) && !isPagesType(sourceType) && (
        <ResolveFromSharePoint
          editor={editor}
          showIdFields={showIdFields}
          onToggleIdFields={mode === "create" ? () => setShowIdFields((v) => !v) : undefined}
        />
      )}

      {sourceType === "blob" && (
        <FieldRow
          error={errors.input_container}
          id="input_container"
          label={t("adminDashboard.libraries.editor.containerName", "Container name")}
          required={mode === "create"}
        >
          <Input
            id="input_container"
            value={str(config.input_container)}
            placeholder={t(
              "adminDashboard.libraries.editor.containerNamePlaceholder",
              "e.g., documents"
            )}
            onChange={(event) => editor.updateSourceConfig("input_container", event.target.value)}
          />
        </FieldRow>
      )}

      {sourceType === "dynamics" && (
        <>
          <FieldRow
            error={errors.environment_url}
            id="environment_url"
            label={t("adminDashboard.libraries.editor.environmentUrl", "Environment URL")}
            required={mode === "create"}
          >
            <Input
              id="environment_url"
              placeholder="https://org.crm.dynamics.com"
              value={str(config.environment_url)}
              onChange={(event) => editor.updateSourceConfig("environment_url", event.target.value)}
            />
          </FieldRow>
          <FieldRow
            error={errors.entity_name}
            id="entity_name"
            label={t("adminDashboard.libraries.editor.entityName", "Entity name")}
            required={mode === "create"}
          >
            <Input
              id="entity_name"
              placeholder="account"
              value={str(config.entity_name)}
              onChange={(event) => editor.updateSourceConfig("entity_name", event.target.value)}
            />
          </FieldRow>
        </>
      )}

      {isExternalType(sourceType) && (
        <div className="bg-muted/50 text-muted-foreground rounded-lg border p-3 text-sm">
          {t(
            "adminDashboard.libraries.editor.externalInfo",
            "External sources are managed via API. Documents are pushed to this library programmatically."
          )}
        </div>
      )}
    </EditorSectionCard>
  )
}

type ResolveFromSharePointProps = {
  editor: LibraryEditor
  showIdFields: boolean
  /** Create mode only: reveal / hide the id inputs to override what was resolved. */
  onToggleIdFields?: () => void
}

/**
 * Looks the site, list and drive ids up through the connection (inspect-sharepoint) and fills
 * them in, so nobody types GUIDs. Shows what was resolved; in create mode the id inputs stay
 * hidden unless the admin chooses to override.
 */
function ResolveFromSharePoint({
  editor,
  showIdFields,
  onToggleIdFields,
}: ResolveFromSharePointProps) {
  const { t } = useTranslation()
  const { inspect } = editor
  const result = inspect.result
  const loading = inspect.status === "loading"

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="space-y-0.5">
          <p className="text-sm font-medium">
            {t("adminDashboard.libraries.editor.resolve.title", "Resolve ids from SharePoint")}
          </p>
          <p className="text-muted-foreground text-xs">
            {t(
              "adminDashboard.libraries.editor.resolve.help",
              "Looks up the site, list and drive ids through the connection and fills them in."
            )}
          </p>
        </div>
        <Button
          disabled={!editor.canInspect || loading}
          size="sm"
          type="button"
          variant="outline"
          onClick={() => void editor.handleResolveIds()}
        >
          {loading ? <Spinner /> : <Search className="size-3.5" />}
          {t("adminDashboard.libraries.editor.resolve.action", "Resolve")}
        </Button>
      </div>
      {!editor.canInspect && (
        <p className="text-muted-foreground text-xs">
          {t(
            "adminDashboard.libraries.editor.resolve.needsAuth",
            "Needs the hostname, site and the credentials from the Connection section."
          )}
        </p>
      )}
      {inspect.status === "error" && inspect.error && (
        <p className="text-destructive text-xs">{inspect.error}</p>
      )}
      {result && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
          <dt className="text-muted-foreground">
            {t("adminDashboard.libraries.editor.resolve.list", "List")}
          </dt>
          <dd>
            {result.list_display_name || result.list_name}
            <span className="text-muted-foreground">
              {" "}
              · {result.list_template || result.item_kind}
            </span>
          </dd>
          <dt className="text-muted-foreground">
            {t("adminDashboard.libraries.editor.resolve.siteId", "Site ID")}
          </dt>
          <dd className="font-mono break-all">{result.site_id}</dd>
          <dt className="text-muted-foreground">
            {t("adminDashboard.libraries.editor.resolve.listId", "List ID")}
          </dt>
          <dd className="font-mono break-all">{result.list_id}</dd>
          {result.drive_id && (
            <>
              <dt className="text-muted-foreground">
                {t("adminDashboard.libraries.editor.resolve.driveId", "Drive ID")}
              </dt>
              <dd className="font-mono break-all">{result.drive_id}</dd>
            </>
          )}
        </dl>
      )}
      {onToggleIdFields && (
        <Button className="px-0" size="sm" type="button" variant="link" onClick={onToggleIdFields}>
          {showIdFields
            ? t("adminDashboard.libraries.editor.resolve.hideIds", "Hide id fields")
            : t("adminDashboard.libraries.editor.resolve.editIds", "Enter ids manually")}
        </Button>
      )}
    </div>
  )
}
